import { Component } from 'react';
import { logAppError } from '../lib/errorReporter';
import { getTraceContext } from '../lib/observability';
import { useT } from '../lib/i18n/I18nContext';
import { writeBreadcrumb } from '../lib/chunkReloadBreadcrumb';

/**
 * ErrorBoundary — catches render errors in child tree.
 *
 * Props:
 *   - fallbackMessage (string): Custom message for the error screen
 *   - viewName (string): Name of the view for logging (e.g. "Planner")
 *   - onRetry (function): Optional retry handler; if provided, shows "Try Again" instead of "Reload App"
 *   - fullScreen (boolean): If true, renders full-screen error (app-level). Default: false (inline).
 *
 * Exported as a functional wrapper that supplies `t` from useT() as a prop
 * to the underlying class component. Hooks cannot be called in class bodies,
 * so any i18n must be resolved outside the class and passed in.
 */
class ErrorBoundaryClass extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    const label = this.props.viewName || 'App';
    console.error(`[ErrorBoundary:${label}]`, error.message, info.componentStack?.split('\n')[1]);
    let ctx = {};
    try { ctx = getTraceContext(); } catch { /* noop */ }
    logAppError({
      type: 'ui_runtime_error',
      message: error.message,
      stack: error.stack,
      view: this.props.viewName,
      metadata: {
        phase_tag: 'phase_a2',
        caught_by: 'error_boundary',
        component_name: this.props.viewName || 'unknown',
        component_stack: (info?.componentStack || '').slice(0, 500),
        ...ctx,
      },
    });

    // Auto-reload on stale chunk errors (deploy invalidated old JS chunks)
    if (ErrorBoundaryClass.isChunkLoadError(error)) {
      console.warn('[ErrorBoundary] Stale chunk detected — reloading app');
      const reloadKey = 'chunk_reload_ts';
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();
      const fromView = this.props.viewName || 'unknown';
      // Only auto-reload once per 30s to prevent infinite reload loops
      if (!lastReload || now - Number(lastReload) > 30000) {
        sessionStorage.setItem(reloadKey, String(now));
        // TICKET-019 Phase 1 — structured observability for chunk-reload events.
        // Before this, the only signal was console.warn (lost on reload) +
        // a generic ui_runtime_error row whose message had to be regex-matched
        // to identify the chunk-reload class. Now every reload writes a
        // dedicated chunk_reload_triggered row with from_view tagged so we
        // can query "how many reloads happened in the last hour, from which
        // views, did recovery succeed?" in app_error_logs.
        logAppError({
          type: 'chunk_reload_triggered',
          message: `Chunk reload from ${fromView}: ${error.message}`,
          view: fromView,
          metadata: {
            from_view: fromView,
            error_name: error.name,
            error_message: error.message,
            rate_limited: false,
            ...ctx,
          },
        });
        // sessionStorage breadcrumb — read on next boot to log a
        // chunk_reload_completed event with time_to_recover_ms. Phase 2
        // will additionally use this to restore the user's previous view
        // instead of dumping them on Focus default.
        writeBreadcrumb({ from_view: fromView, error_msg: error.message });
        window.location.reload();
        return;
      }
      // Rate-limited path — log so silent suppression is visible. Without
      // this, the inline error UI sticks up but no one knows the watchdog
      // skipped a reload because of the 30s cooldown.
      const ms_since_last = now - Number(lastReload);
      logAppError({
        type: 'chunk_reload_skipped',
        message: `Chunk reload skipped (rate-limited ${ms_since_last}ms since last)`,
        view: fromView,
        metadata: {
          from_view: fromView,
          ms_since_last,
          rate_limited: true,
          error_name: error.name,
          error_message: error.message,
          ...ctx,
        },
      });
    }
  }

  static isChunkLoadError(error) {
    if (!error) return false;
    const msg = error.message || '';
    return (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('is not a valid JavaScript MIME type') ||
      msg.includes('Loading chunk') ||
      msg.includes('ChunkLoadError') ||
      (error.name === 'TypeError' && msg.includes('text/html'))
    );
  }

  componentDidUpdate(prevProps) {
    // Reset error state when resetKeys change (e.g., tab switch)
    if (this.state.hasError && this.props.resetKeys) {
      const prev = prevProps.resetKeys || [];
      const curr = this.props.resetKeys || [];
      if (prev.length !== curr.length || prev.some((k, i) => k !== curr[i])) {
        this.setState({ hasError: false, error: null });
      }
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onRetry) this.props.onRetry();
  };

  render() {
    if (this.state.hasError) {
      const { fullScreen, fallbackMessage, viewName, t } = this.props;
      const tt = typeof t === 'function' ? t : ((_k, fb) => fb);

      // Inline (per-view) error display
      if (!fullScreen) {
        return (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: 48, gap: 12, minHeight: 200,
          }}>
            <div style={{ fontSize: 32, color: 'var(--bad, #E53E3E)' }}>!</div>
            <h3 style={{ fontSize: 15, color: 'var(--t1, #1C3F75)', margin: 0 }}>
              {viewName
                ? tt('error.viewFailed', `${viewName} failed to load`).replace('{view}', viewName)
                : tt('error.somethingWentWrong', 'Something went wrong')}
            </h3>
            <p style={{ fontSize: 12, color: 'var(--tm, #58595B)', margin: 0, textAlign: 'center', maxWidth: 280 }}>
              {fallbackMessage || this.state.error?.message || tt('error.unexpected', 'An unexpected error occurred')}
            </p>
            <button
              onClick={this.handleRetry}
              style={{
                padding: '8px 20px', borderRadius: 8, border: '1px solid var(--bdr, #ccc)',
                background: 'var(--card, #fff)', color: 'var(--accent, #1C3F75)', fontSize: 13,
                fontWeight: 600, cursor: 'pointer', marginTop: 4,
              }}
            >
              {tt('common.tryAgain', 'Try Again')}
            </button>
          </div>
        );
      }

      // Full-screen (app-level) error display
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--grad, linear-gradient(145deg, #EEF2FF 0%, #F0F9FF 100%))',
          fontFamily: "-apple-system, 'SF Pro Display', sans-serif",
        }}>
          <div style={{ textAlign: 'center', padding: 24 }}>
            <div style={{ fontSize: 48, marginBottom: 16, color: 'var(--bad, #E53E3E)' }}>!</div>
            <h2 style={{ fontSize: 18, color: 'var(--t1, #1C3F75)', marginBottom: 8 }}>{tt('error.somethingWentWrong', 'Something went wrong')}</h2>
            <p style={{ fontSize: 13, color: 'var(--tm, #58595B)', marginBottom: 24 }}>
              {this.state.error?.message || tt('error.unexpected', 'An unexpected error occurred')}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 24px', borderRadius: 8, border: 'none',
                background: 'var(--navy, #1C3F75)', color: 'var(--card)', fontSize: 14,
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              {tt('common.reloadApp', 'Reload App')}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Functional wrapper that supplies `t` as a prop. Keeps the class isolated
 * from hook rules. If useT() itself throws (context missing), the fallback
 * `t` inside the class still renders English text — no double-fault.
 */
export default function ErrorBoundary(props) {
  // Wrap useT() in try/catch so the ErrorBoundary still mounts even if the
  // I18nProvider itself crashed (avoids double-fault white screens). The hook
  // call IS unconditional — the try/catch only handles the throw inside the
  // implementation, not whether the hook is called. ESLint can't distinguish
  // this from a conditional call.
  // eslint-disable-next-line react-hooks/rules-of-hooks -- intentional fallback for double-fault protection
  let t; try { t = useT().t; } catch { t = null; }
  return <ErrorBoundaryClass {...props} t={t} />;
}
